/**
 * Textos fijos de la caja «Ante el SAT» de la ficha y del anexo del PDF del Buscador (corte 2 de los certificados del SAT; diseño
 * docs/producto/diseno-ficha-certificados-sat.md, §2.0, §8 y §14). Las frases de la caja (S01 a S31 y Z1 a Z15) vienen del documento,
 * de la base (`nexo.sat_certificado_texto`); aquí sólo van los rótulos que la pantalla pone alrededor. Vive en el servicio (lo usa el
 * PDF, y el servicio no alcanza app/shared al correr); las pantallas lo importan por app/shared/rotulos/certificados.ts.
 *
 * Estado de aprobación: aprobados por Bernardo el 2026-10-16 los rótulos de los datos (S01), «según el Registro Nacional de Población»
 * (S02), «Ver todos los certificados» (S19), «Certificado del SAT» como fuente (decisión 9) y, en el corte 2 («De acuerdo con tus
 * recomendaciones», por la sesión coordinadora), los títulos de la caja, sus tres partes, la marca «posible» con su causa y las columnas
 * del detalle; el 2026-10-16 («Ok», por la coordinadora), también la ayuda de la marca «posible», «en la lista de OFAC», el título,
 * el aviso y el botón de cerrar del detalle, la fuente de la consulta de la CURP, y las voces del camino y de la línea de tiempo con los
 * certificados. Ninguno queda por aprobar.
 */
export const SAT = {
  // Aprobados (corte 2): el título de la caja.
  tituloEmpresa: 'Quién la representa ante el SAT',
  tituloPersona: 'Ante el SAT',
  // Aprobados (corte 2): las tres partes de cada tarjeta (§2.0).
  quienEs: 'Quién es',
  queRepresenta: 'Qué empresas representa ante el SAT',
  conQuien: 'Con quién más actúa',
  // S02 (aprobado).
  segunPoblacion: 'según el Registro Nacional de Población',
  // Aprobado (2026-10-16): la marca de la lista junto al nombre (maqueta: «Juana Ejemplo Ruiz en la lista de OFAC»).
  enLaLista: 'en la lista de OFAC',
  // Aprobada (2026-10-16, de la maqueta): la ayuda de la marca «posible» en la caja.
  posibleAyuda: '«Posible» quiere decir que los datos no bastan para confirmarlo. No cambia el nivel.',
  // S19 (aprobado): el botón del detalle.
  verTodos: 'Ver todos los certificados',
  // Aprobados (2026-10-16): el detalle de los certificados (§8: número de serie, sucursal y vigencia sólo aquí).
  ocultarTodos: 'Ocultar los certificados',
  tituloDetalle: 'Los certificados del SAT',
  mostrados: 'Se muestran {n} de {total} certificados.',
  sinCertificados: 'No encontramos certificados del SAT.',
  // Aprobadas (corte 2): las columnas del detalle.
  colEmpresa: 'Empresa',
  colRepresentante: 'Representante',
  colEmitido: 'Emitido',
  colVence: 'Vence',
  colSerie: 'Número de serie',
  colSucursal: 'Sucursal',
  colCorreo: 'Correo',
  // Decisión 9 (aprobado): de dónde sale un dato o una frase que no se abre.
  fuenteCertificado: 'Certificado del SAT',
  // Aprobada (2026-10-16): la fuente de un dato de la consulta de la CURP (S02 dice «según el Registro Nacional de Población»).
  fuenteConsulta: 'Registro Nacional de Población',
} as const;

/**
 * S03 (aprobado): el título de los datos de la persona cuando vienen también de la consulta de la CURP o de un certificado del SAT (los
 * de siempre, FICHA.datosActas, datosLista y datosListaYActas, cuando no).
 */
export const DATOS_POBLACION = {
  solo: 'Lo que dice el Registro Nacional de Población de esta persona',
  actas: 'Lo que dicen las actas y el Registro Nacional de Población de esta persona',
  lista: 'Lo que dicen la lista de OFAC y el Registro Nacional de Población de esta persona',
  listaYActas: 'Lo que dicen la lista de OFAC, las actas y el Registro Nacional de Población de esta persona',
} as const;

/**
 * Corte 4a (aprobados por Bernardo el 2026-10-17, diseño §20, «Textos nuevos»): la persona que sólo aparece en los certificados del SAT,
 * como representante de una empresa de la lista (clase sat_rep). `fila`: lo que dice la fila del buscador después de «Persona»
 * («Persona · en los certificados del SAT»); `datos`: de dónde salen sus datos en la ficha; `aviso`: lo que todavía no se ve (4b).
 */
export const SAT_REP = {
  fila: 'en los certificados del SAT',
  datos: 'Según los certificados del SAT y el Registro Nacional de Población',
  aviso: 'Esta persona no aparece en las actas de Argus ni en la lista de OFAC; su relación sale de los certificados del SAT. Todavía no se puede asociar a un caso.',
  // Corte 4b-1 (T2, aprobado por Bernardo el 2026-10-19, «Sí a todo»): el aviso de quien la lista tiene sólo como posible
  avisoPosible: 'Esta persona no aparece en las actas de Argus; su relación sale de los certificados del SAT. Puede ser alguien de la lista de OFAC: falta confirmarlo.',
} as const;

/**
 * La marca «posible» en el PDF del Buscador: la misma palabra que MARCA_POSIBLE de app/shared/rotulos/buscador.ts (aprobada el
 * 2026-10-04), que el servicio no alcanza al correr. Copia vigilada por la prueba del anexo (test/ante-el-sat.test.ts).
 */
export const MARCA_POSIBLE_SAT = 'posible';

/** S01 (aprobado): los rótulos de los datos de una persona en la caja. */
export const DATO_SAT = {
  nombre: 'Nombre',
  curp: 'CURP',
  rfc: 'RFC',
  nacimiento: 'Nació',
  lugar_nacimiento: 'Estado de nacimiento',
  sexo: 'Sexo',
  correo: 'Correo ante el SAT',
} as const;

/**
 * Aprobadas (2026-10-16): «Cómo se relaciona», según quién lo dice, ahora que un certificado del SAT también relaciona. Los de actas y
 * de la lista de OFAC son los textos 14 de siempre (FICHA.segunActas, segunOfac y segunActasYOfac).
 */
export const VOCES_SAT = {
  sat: 'Según certificados del SAT',
  actasYSat: 'Según actas del Registro Público de Comercio y certificados del SAT',
  ofacYSat: 'Según la lista de OFAC y certificados del SAT',
  todas: 'Según actas del Registro Público de Comercio, la lista de OFAC y certificados del SAT',
} as const;

/** Aprobados (2026-10-16): el subtítulo de la línea de tiempo cuando hay certificados del SAT (el de siempre: ROTULOS.subtituloTiempo). */
export const TIEMPO_SAT = {
  sat: 'Fechas de los certificados del SAT y de la lista de OFAC',
  actasYSat: 'Fechas de las actas, de los certificados del SAT y de la lista de OFAC',
} as const;

/** Todos los textos del catálogo, para las pruebas (W2 y el glosario). */
export const textosCertificados = (): string[] => [...Object.values(SAT), ...Object.values(DATO_SAT), ...Object.values(VOCES_SAT), ...Object.values(DATOS_POBLACION), ...Object.values(TIEMPO_SAT), ...Object.values(SAT_REP)];
